/**
 * Mock handlers for agenda service
 * Simulates backend API responses with in-memory state persistence
 */

import {
  getEventsState,
  setEventsState,
  getNextEventId,
} from '../data/agenda';
import { getRandomDelay } from '../config';

/**
 * Simulate async delay for realistic network behavior
 * @param {number} ms - Milliseconds to delay
 * @returns {Promise}
 */
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// ============================================================
// EVENT CRUD OPERATIONS
// ============================================================

/**
 * Mock: GET /api/agenda/events/
 * @param {Object} params - Query parameters (search, type, start_date, end_date, etc.)
 * @returns {Promise<{data: Array}>}
 */
export const mockGetEvents = async (params = {}) => {
  await delay(getRandomDelay());

  let events = [...getEventsState()];

  // Apply search filter
  if (params.search) {
    const searchLower = params.search.toLowerCase();
    events = events.filter(e =>
      e.title?.toLowerCase().includes(searchLower) ||
      e.description?.toLowerCase().includes(searchLower) ||
      e.location?.toLowerCase().includes(searchLower) ||
      e.participants?.toLowerCase().includes(searchLower)
    );
  }

  // Apply type filter
  if (params.type) {
    events = events.filter(e => e.type === params.type);
  }

  // Apply date range filter
  if (params.start_date) {
    events = events.filter(e => e.start_date >= params.start_date);
  }
  if (params.end_date) {
    events = events.filter(e => e.start_date <= params.end_date);
  }

  // Sort by start_date and start_time ascending
  events.sort((a, b) => {
    const dateCompare = a.start_date.localeCompare(b.start_date);
    if (dateCompare !== 0) return dateCompare;
    return a.start_time.localeCompare(b.start_time);
  });

  return { data: events };
};

/**
 * Mock: GET /api/agenda/events/{id}/
 * @param {number|string} id - Event ID
 * @returns {Promise<{data: Object}>}
 */
export const mockGetEvent = async (id) => {
  await delay(getRandomDelay());

  const event = getEventsState().find(e => e.id === parseInt(id));

  if (!event) {
    throw {
      response: {
        status: 404,
        data: { detail: 'Event not found' }
      }
    };
  }

  return { data: event };
};

/**
 * Mock: POST /api/agenda/events/
 * @param {Object} data - Event data
 * @returns {Promise<{data: Object}>}
 */
export const mockCreateEvent = async (data) => {
  await delay(getRandomDelay());

  const events = getEventsState();
  const newId = getNextEventId();
  const now = new Date().toISOString();

  const newEvent = {
    id: newId,
    title: data.title || '',
    type: data.type || 'meeting',
    start_date: data.start_date || '',
    end_date: data.end_date || null,
    start_time: data.start_time || '09:00',
    end_time: data.end_time || null,
    location: data.location || '',
    participants: data.participants || '',
    description: data.description || '',
    send_reminder: data.send_reminder || false,
    is_recurring: data.is_recurring || false,
    add_video_link: data.add_video_link || false,
    created_at: now,
    updated_at: now,
  };

  setEventsState([...events, newEvent]);
  return { data: newEvent };
};

/**
 * Mock: PUT /api/agenda/events/{id}/
 * @param {number|string} id - Event ID
 * @param {Object} data - Updated event data
 * @returns {Promise<{data: Object}>}
 */
export const mockUpdateEvent = async (id, data) => {
  await delay(getRandomDelay());

  const events = getEventsState();
  const eventIndex = events.findIndex(e => e.id === parseInt(id));

  if (eventIndex === -1) {
    throw {
      response: {
        status: 404,
        data: { detail: 'Event not found' }
      }
    };
  }

  const existingEvent = events[eventIndex];
  const now = new Date().toISOString();

  const updatedEvent = {
    ...existingEvent,
    title: data.title !== undefined ? data.title : existingEvent.title,
    type: data.type !== undefined ? data.type : existingEvent.type,
    start_date: data.start_date !== undefined ? data.start_date : existingEvent.start_date,
    end_date: data.end_date !== undefined ? data.end_date : existingEvent.end_date,
    start_time: data.start_time !== undefined ? data.start_time : existingEvent.start_time,
    end_time: data.end_time !== undefined ? data.end_time : existingEvent.end_time,
    location: data.location !== undefined ? data.location : existingEvent.location,
    participants: data.participants !== undefined ? data.participants : existingEvent.participants,
    description: data.description !== undefined ? data.description : existingEvent.description,
    send_reminder: data.send_reminder !== undefined ? data.send_reminder : existingEvent.send_reminder,
    is_recurring: data.is_recurring !== undefined ? data.is_recurring : existingEvent.is_recurring,
    add_video_link: data.add_video_link !== undefined ? data.add_video_link : existingEvent.add_video_link,
    updated_at: now,
  };

  const newEvents = [...events];
  newEvents[eventIndex] = updatedEvent;
  setEventsState(newEvents);

  return { data: updatedEvent };
};

/**
 * Mock: PATCH /api/agenda/events/{id}/
 * @param {number|string} id - Event ID
 * @param {Object} data - Partial event data
 * @returns {Promise<{data: Object}>}
 */
export const mockPatchEvent = async (id, data) => {
  // PATCH is same as PUT for our mock
  return mockUpdateEvent(id, data);
};

/**
 * Mock: DELETE /api/agenda/events/{id}/
 * @param {number|string} id - Event ID
 * @returns {Promise<{data: null}>}
 */
export const mockDeleteEvent = async (id) => {
  await delay(getRandomDelay());

  const events = getEventsState();
  const newEvents = events.filter(e => e.id !== parseInt(id));

  if (events.length === newEvents.length) {
    throw {
      response: {
        status: 404,
        data: { detail: 'Event not found' }
      }
    };
  }

  setEventsState(newEvents);
  return { data: null };
};

// ============================================================
// EXPORT ALL HANDLERS
// ============================================================

export default {
  mockGetEvents,
  mockGetEvent,
  mockCreateEvent,
  mockUpdateEvent,
  mockPatchEvent,
  mockDeleteEvent,
};
